import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Clock3,
  Eye,
  LockKeyhole,
  Mail,
  MapPin,
  PackageCheck,
  ShieldCheck,
  ShoppingBag,
  Truck,
  UserRound,
  X,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { api } from "../services/api";

const fieldClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#3991D5] focus:ring-4 focus:ring-[#70C6FE]/20";

const primaryButtonClass =
  "inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#3991D5] px-5 py-3.5 text-sm font-bold text-white transition hover:bg-[#1a6db5] focus:outline-none focus:ring-4 focus:ring-[#70C6FE]/30";

const secondaryButtonClass =
  "inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#3991D5]/25 bg-white px-5 py-3.5 text-sm font-bold text-[#1278b5] transition hover:border-[#3991D5] hover:bg-[#f0f8ff] focus:outline-none focus:ring-4 focus:ring-[#70C6FE]/20";

function Account() {
  const navigate = useNavigate();
  const { isAdmin, loading, login, logout, register, user } = useAuth();
  const [authModal, setAuthModal] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const openAuthModal = (mode) => {
    setAuthModal(mode);
    setError("");
    setMessage("");
  };

  const closeAuthModal = () => {
    setAuthModal(null);
    setError("");
    setMessage("");
  };

  const redirectForRole = (user) => {
    navigate(user.role === "admin" ? "/dashboard" : "/my-account");
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    const formData = new FormData(event.currentTarget);

    try {
      const user = await login(Object.fromEntries(formData.entries()));
      redirectForRole(user);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setError("");
    const formData = new FormData(event.currentTarget);

    try {
      const user = await register(Object.fromEntries(formData.entries()));
      redirectForRole(user);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    const formData = new FormData(event.currentTarget);

    try {
      const data = await api.post("/auth/forgot-password", Object.fromEntries(formData.entries()));
      setMessage(data.message || "If an account exists, password reset instructions will be sent.");
    } catch (err) {
      setError(err.message);
    }
  };

  const accountStats = [
    { label: "Average dispatch", value: "24h", icon: Clock3 },
    { label: "Active orders", value: "03", icon: PackageCheck },
    { label: "Saved addresses", value: "02", icon: MapPin },
  ];

  const benefits = [
    {
      title: "Faster checkout",
      text: "Save billing, shipping, and preferred contact details for repeat parts orders.",
      icon: ShoppingBag,
    },
    {
      title: "Order visibility",
      text: "Track order status, shipment updates, invoices, and reorder history from one place.",
      icon: Truck,
    },
    {
      title: "Secure account",
      text: "Keep purchase details protected with a modern password-first account flow.",
      icon: ShieldCheck,
    },
  ];

  if (loading) {
    return (
      <section className="bg-[#f7fbff] px-4 py-16 text-slate-900 sm:px-6">
        <div className="container">
          <div className="rounded-[24px] border border-[#3991D5]/10 bg-white p-8 text-sm font-semibold text-slate-600 shadow-sm">
            Loading account...
          </div>
        </div>
      </section>
    );
  }

  if (user) {
    const destination = isAdmin ? "/dashboard" : "/my-account";

    return (
      <section className="bg-[#f7fbff] px-4 py-12 text-slate-900 sm:px-6 lg:py-16">
        <div className="container">
          <div className="grid gap-6 rounded-[28px] border border-[#3991D5]/10 bg-white p-6 shadow-[0_18px_60px_rgba(57,145,213,0.10)] lg:grid-cols-[1fr_auto] lg:items-center lg:p-8">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#e8f6ff] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#1278b5]">
                <BadgeCheck className="h-4 w-4" />
                Signed in
              </div>
              <h1 className="mt-5 font-gopher text-4xl font-black uppercase">
                Welcome back, {user.name}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                You are signed in as {isAdmin ? "an admin" : "a customer"}. Continue to your
                {isAdmin ? " dashboard" : " account area"} or sign out before using a different account.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:min-w-80 lg:grid-cols-1">
              <Link className={primaryButtonClass} to={destination}>
                {isAdmin ? "Open admin dashboard" : "Open my account"}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                className={secondaryButtonClass}
                onClick={() => {
                  logout();
                  navigate("/account");
                }}
                type="button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#f7fbff] px-4 py-10 text-slate-900 sm:px-6 lg:py-14">
      <div className="container">
        <div className="grid gap-8 lg:grid-cols-[0.92fr_1.08fr] lg:items-start">
          <div className="space-y-6">
            <div className="rounded-[28px] bg-[#0f172a] p-6 text-white shadow-[0_24px_70px_rgba(15,23,42,0.18)] sm:p-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#BFE8FF]">
                <BadgeCheck className="h-4 w-4" />
                Account center
              </div>

              <h1 className="mt-6 font-gopher text-4xl font-black uppercase leading-tight sm:text-5xl">
                Manage every order from one clean place
              </h1>

              <p className="mt-4 max-w-xl text-base leading-7 text-slate-300">
                Sign in, create an account, or continue as a guest for Bobbin USA
                parts, bobbins, needles, and machine supplies.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {accountStats.map((item) => {
                  const Icon = item.icon;

                  return (
                    <div
                      className="rounded-2xl border border-white/10 bg-white/[0.06] p-4"
                      key={item.label}
                    >
                      <Icon className="h-5 w-5 text-[#70C6FE]" />
                      <div className="mt-4 font-gopher text-3xl font-black">
                        {item.value}
                      </div>
                      <div className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                        {item.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
              {benefits.map((benefit) => {
                const Icon = benefit.icon;

                return (
                  <div
                    className="flex gap-4 rounded-2xl border border-[#3991D5]/10 bg-white p-5 shadow-sm"
                    key={benefit.title}
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#e8f6ff] text-[#1278b5]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-gopher text-lg font-bold text-slate-950">
                        {benefit.title}
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-slate-600">
                        {benefit.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-[28px] border border-[#3991D5]/10 bg-white p-6 shadow-[0_18px_60px_rgba(57,145,213,0.10)] sm:p-8">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#3991D5] text-white">
              <ShoppingBag className="h-6 w-6" />
            </div>
            <h2 className="mt-6 font-gopher text-3xl font-black uppercase text-slate-950">
              Continue your order
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Checkout as a guest for a quick one-time purchase, or sign in to use saved addresses, invoices, and order history.
            </p>

            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              <button className={primaryButtonClass} onClick={() => openAuthModal("login")} type="button">
                Sign in
                <UserRound className="h-4 w-4" />
              </button>
              <button
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-3.5 text-sm font-bold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-300"
                onClick={() => openAuthModal("register")}
                type="button"
              >
                Register
                <BadgeCheck className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <h3 className="font-gopher text-lg font-black uppercase text-slate-950">
                Guest checkout
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Best for quick replacement parts and one-time orders.
              </p>
              <Link
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-[#3991D5]/40 hover:bg-[#f7fbff] hover:text-[#1278b5]"
                to="/all-products"
              >
                Continue as guest
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {authModal && (
        <AuthModal
          error={error}
          message={message}
          mode={authModal}
          onClose={closeAuthModal}
          onForgot={() => openAuthModal("forgot")}
          onLogin={handleLogin}
          onRegister={handleRegister}
          onReset={handleForgotPassword}
          onSwitch={openAuthModal}
        />
      )}
    </section>
  );
}

export default Account;

function AuthModal({ error, message, mode, onClose, onForgot, onLogin, onRegister, onReset, onSwitch }) {
  const isLogin = mode === "login";
  const isRegister = mode === "register";
  const isForgot = mode === "forgot";
  const title = isLogin ? "Sign in" : isRegister ? "Create account" : "Reset password";
  const description = isLogin
    ? "Access saved details, order history, and tracking."
    : isRegister
      ? "Set up faster checkout for future ecommerce orders."
      : "Enter your account email and we will send a secure reset link.";

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/60 px-4 py-6 backdrop-blur-sm">
      <div className="max-h-[calc(100vh-3rem)] w-full max-w-lg overflow-y-auto rounded-[28px] bg-white p-5 shadow-[0_28px_90px_rgba(15,23,42,0.35)] sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#e8f6ff] px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#1278b5]">
              Account
            </div>
            <h2 className="mt-4 font-gopher text-3xl font-black uppercase text-slate-950">
              {title}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {description}
            </p>
          </div>
          <button
            aria-label="Close account popup"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-950"
            onClick={onClose}
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {(error || message) && (
          <div
            className={`mt-5 rounded-xl px-4 py-3 text-sm font-semibold ${
              error
                ? "border border-red-200 bg-red-50 text-red-700"
                : "border border-emerald-200 bg-emerald-50 text-emerald-800"
            }`}
          >
            {error || message}
          </div>
        )}

        {isLogin && (
          <form className="mt-6" onSubmit={onLogin}>
            <div className="space-y-4">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Email address</span>
                <span className="relative block">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input className={`${fieldClass} pl-11`} name="email" placeholder="you@example.com" type="email" required />
                </span>
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Password</span>
                <span className="relative block">
                  <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input className={`${fieldClass} pl-11 pr-11`} name="password" placeholder="Enter password" type="password" required />
                  <Eye className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </span>
              </label>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
              <label className="flex items-center gap-2 text-slate-600">
                <input className="h-4 w-4 rounded border-slate-300 text-[#3991D5] focus:ring-[#3991D5]" type="checkbox" />
                Remember me
              </label>
              <button className="font-semibold text-[#1278b5] hover:text-[#0f5f90]" onClick={onForgot} type="button">
                Forgot password?
              </button>
            </div>

            <button className={`${primaryButtonClass} mt-6`} type="submit">
              Sign in
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        )}

        {isRegister && (
          <form className="mt-6" onSubmit={onRegister}>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Full name</span>
                <input className={fieldClass} name="name" placeholder="First and last name" type="text" required />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Email address</span>
                <input className={fieldClass} name="email" placeholder="you@example.com" type="email" required />
              </label>
            </div>
            <label className="mt-4 block">
              <span className="mb-2 block text-sm font-semibold text-slate-700">Password</span>
              <input className={fieldClass} name="password" placeholder="Create password" type="password" required />
            </label>
            <label className="mt-4 flex items-start gap-3 text-sm leading-6 text-slate-600">
              <input className="mt-1 h-4 w-4 rounded border-slate-300 text-[#3991D5] focus:ring-[#3991D5]" type="checkbox" required />
              Email me order updates and product availability alerts.
            </label>

            <button className={`${primaryButtonClass} mt-6`} type="submit">
              Create account
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        )}

        {isForgot && (
          <form className="mt-6" onSubmit={onReset}>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-700">Account email</span>
              <input className={fieldClass} name="email" placeholder="you@example.com" type="email" required />
            </label>
            <button className={`${primaryButtonClass} mt-5`} type="submit">
              Send reset link
              <PackageCheck className="h-4 w-4" />
            </button>
          </form>
        )}

        <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-center text-sm font-semibold text-slate-600">
          {isLogin ? (
            <>
              New customer?{" "}
              <button className="font-bold text-[#1278b5]" onClick={() => onSwitch("register")} type="button">
                Create an account
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button className="font-bold text-[#1278b5]" onClick={() => onSwitch("login")} type="button">
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
